import React, { FC, Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { Box, ButtonGroup, CircularProgress, IconButton, Typography } from '@mui/material';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import DownloadIcon from '@mui/icons-material/Download';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { useTranslation } from 'react-i18next';

type DocumentRenderKind = 'IMAGE' | 'HTML' | 'PDF' | 'UNSUPPORTED';

type DocumentViewerProps = {
    url: string;
    contentType: string;
    filename?: string;
    height: string;
    /** Width of the rendered HTML document box. Defaults to 430. */
    contentWidth?: number;
    /**
     * CSS zoom applied to the HTML document. Unlike a transform scale this
     * affects layout, so the document keeps its full natural height (no
     * inner scrollbox) and the viewer shrink-wraps the zoomed size.
     * Defaults to 1.
     */
    htmlZoom?: number;
    testIdPrefix: string;
    overlay?: ReactNode;
    toolbarExtra?: ReactNode;
    /** Degrees to turn an image clockwise when it opens (e.g. a sideways photo of a form). */
    initialRotation?: number;
    /**
     * Widest an image may be shown (CSS length). The image is sized to fit both
     * `height` and this, so a wide scan cannot push the surrounding layout.
     */
    maxImageWidth?: string;
};

const resolveRenderKind = (contentType: string): DocumentRenderKind => {
    if (contentType.startsWith('image/')) return 'IMAGE';
    if (contentType === 'text/html') return 'HTML';
    if (contentType === 'application/pdf') return 'PDF';
    return 'UNSUPPORTED';
};

// S3 file URLs are presigned with Content-Disposition: attachment, which browsers
// honor for iframe/embed navigations (unlike <img>) and would trigger a download
// instead of rendering. Fetching HTML/PDF content ourselves sidesteps that.
const useFetchedDocumentContent = (url: string, renderKind: DocumentRenderKind) => {
    const [htmlContent, setHtmlContent] = useState<string>();
    const [objectUrl, setObjectUrl] = useState<string>();
    const [isFetching, setIsFetching] = useState(renderKind === 'HTML' || renderKind === 'PDF');

    useEffect(() => {
        if (renderKind !== 'HTML' && renderKind !== 'PDF') {
            return undefined;
        }

        let createdObjectUrl: string | undefined;
        setIsFetching(true);
        (async () => {
            try {
                const response = await fetch(url);
                if (renderKind === 'HTML') {
                    setHtmlContent(await response.text());
                } else {
                    const blob = await response.blob();
                    createdObjectUrl = URL.createObjectURL(blob);
                    setObjectUrl(createdObjectUrl);
                }
            } finally {
                setIsFetching(false);
            }
        })();

        return () => {
            if (createdObjectUrl) {
                URL.revokeObjectURL(createdObjectUrl);
            }
        };
    }, [url, renderKind]);

    return { htmlContent, objectUrl, isFetching };
};

// react-zoom-pan-pinch reads this class to skip wheel/panning handling on this
// element, so scrolling over it runs the browser's native scroll instead of
// being captured for zoom/pan.
const SCROLLABLE_HTML_CONTENT_CLASS = 'document-viewer-scrollable-html-content';

type ShadowHtmlContentProps = {
    htmlContent: string;
    width: number;
    zoom: number;
    testIdPrefix: string;
};

// Renders a full standalone HTML document (its own <style>/<body>) inside a shadow
// root instead of an iframe, so its styles stay scoped to this box without the
// overhead of a separate browsing context. Scripts in htmlContent will not execute.
const ShadowHtmlContent: FC<ShadowHtmlContentProps> = ({
    htmlContent,
    width,
    zoom,
    testIdPrefix,
}: ShadowHtmlContentProps) => {
    const shadowHostRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const shadowHost = shadowHostRef.current;
        if (!shadowHost) return;

        const shadowRoot = shadowHost.shadowRoot ?? shadowHost.attachShadow({ mode: 'open' });
        shadowRoot.innerHTML = htmlContent;
    }, [htmlContent]);

    return (
        <div
            ref={shadowHostRef}
            className={SCROLLABLE_HTML_CONTENT_CLASS}
            data-test={`${testIdPrefix}.html-content`}
            style={{
                width,
                zoom,
                background: '#fff',
            }}
        />
    );
};

type RotatableImageProps = {
    url: string;
    height: string;
    maxWidth?: string;
    rotation: number;
    overlay?: ReactNode;
};

// The photo and its overlay turn together — overlay boxes are placed in
// percentages of the photo, so they stay on their values. The frame keeps the
// size of the upright photo whatever the rotation: a quarter-turned picture is
// scaled down to fit inside it, centred (wide forms are often photographed sideways).
const RotatableImage: FC<RotatableImageProps> = ({ url, height, maxWidth, rotation, overlay }: RotatableImageProps) => {
    const [aspectRatio, setAspectRatio] = useState(1); // naturalWidth / naturalHeight
    // The upright photo fits both the viewer's height and the widest it may be shown.
    const imageHeight = maxWidth ? `min(${height}, calc(${maxWidth} / ${aspectRatio}))` : height;
    const isQuarterTurn = rotation % 180 !== 0;
    // Turned a quarter, width and height swap; this factor makes the turned picture fit the frame.
    const quarterTurnScale = Math.min(aspectRatio, 1 / aspectRatio);

    const picture = (
        <Box sx={{ position: 'relative', lineHeight: 0 }}>
            <img
                src={url}
                onLoad={(event) =>
                    setAspectRatio(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight || 1)
                }
                style={{ display: 'block', height: imageHeight, minWidth: 400 }}
            />
            {overlay}
        </Box>
    );

    if (!isQuarterTurn) {
        return <Box sx={{ transform: rotation ? `rotate(${rotation}deg)` : undefined }}>{picture}</Box>;
    }
    return (
        <Box
            sx={{
                position: 'relative',
                height: imageHeight,
                width: `calc(${imageHeight} * ${aspectRatio})`,
                minWidth: 400,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: `translate(-50%, -50%) rotate(${rotation}deg) scale(${quarterTurnScale})`,
                }}
            >
                {picture}
            </Box>
        </Box>
    );
};

// Generic zoomable/downloadable document viewer shared by every "show the source
// document next to the order form" use case (scanner-case printable order forms,
// AI-scanned paper order forms, and any future document type). Callers only need
// to provide a url + contentType; overlay/toolbarExtra let a caller add
// content-specific extras (e.g. AI field-confidence boxes) without this component
// knowing about that domain.
const DocumentViewer: FC<DocumentViewerProps> = ({
    url,
    contentType,
    filename,
    height,
    contentWidth = 430,
    htmlZoom = 1,
    testIdPrefix,
    overlay,
    toolbarExtra,
    initialRotation = 0,
    maxImageWidth,
}: DocumentViewerProps) => {
    const { t: translate } = useTranslation(['order-forms']);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [rotation, setRotation] = useState(initialRotation);
    const renderKind = resolveRenderKind(contentType);
    const { htmlContent, objectUrl, isFetching } = useFetchedDocumentContent(url, renderKind);

    // Every document opens the way it reads upright (as far as the caller knows).
    useEffect(() => setRotation(initialRotation), [url, initialRotation]);

    useEffect(() => {
        const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    return (
        <Box ref={containerRef} sx={isFullscreen ? { height: '100vh', bgcolor: '#666' } : undefined}>
            <TransformWrapper
                centerZoomedOut
                smooth={false}
                minScale={0.5}
                velocityAnimation={{ disabled: true }}
                // Wheel over the HTML content scrolls the document natively;
                // dragging anywhere (content included) pans the zoom viewport.
                // (Text selection inside the document doesn't work regardless,
                // so panning gets the drag gesture.)
                wheel={{ excluded: [SCROLLABLE_HTML_CONTENT_CLASS] }}
            >
                {({ zoomIn, zoomOut, resetTransform }) => (
                    <Fragment>
                        <Box display="flex" alignItems="center">
                            <Box flexGrow={1}>
                                {/* Over HTML content the wheel scrolls natively (excluded from
                                    zoom/pan), so the zoom hint would be misleading there. */}
                                {renderKind !== 'HTML' && (
                                    <Typography variant="caption">
                                        {translate('order-forms:USE_SCROLL_WHEEL_TO_ZOOM')}
                                    </Typography>
                                )}
                            </Box>
                            <ButtonGroup variant="text" size="small">
                                <IconButton href={url} data-test={`${testIdPrefix}.download-btn`} download>
                                    <DownloadIcon fontSize="inherit" sx={{ fontSize: '14px' }} />
                                </IconButton>
                                {toolbarExtra}
                                {renderKind === 'IMAGE' && (
                                    <IconButton
                                        onClick={() => {
                                            setRotation((current) => (current + 90) % 360);
                                            resetTransform();
                                        }}
                                        aria-label={translate('order-forms:ROTATE_IMAGE')}
                                        title={translate('order-forms:ROTATE_IMAGE')}
                                        data-test={`${testIdPrefix}.rotate-btn`}
                                    >
                                        <RotateRightIcon fontSize="inherit" sx={{ fontSize: '14px' }} />
                                    </IconButton>
                                )}
                                <IconButton onClick={() => zoomIn()} data-test={`${testIdPrefix}.zoom-in-btn`}>
                                    <ZoomInIcon fontSize="inherit" sx={{ fontSize: '14px' }} />
                                </IconButton>
                                <IconButton onClick={() => zoomOut()} data-test={`${testIdPrefix}.zoom-out-btn`}>
                                    <ZoomOutIcon fontSize="inherit" sx={{ fontSize: '14px' }} />
                                </IconButton>
                                <IconButton onClick={() => resetTransform()} data-test={`${testIdPrefix}.reset-btn`}>
                                    <CenterFocusWeakIcon fontSize="inherit" sx={{ fontSize: '14px' }} />
                                </IconButton>
                            </ButtonGroup>
                        </Box>
                        <TransformComponent
                            wrapperStyle={{
                                border: '2px solid #666',
                                padding: '5px',
                                background: '#666',
                                cursor: 'grab',
                                // The full-height HTML document scrolls inside the
                                // viewer (wheel events bubble here since the lib
                                // ignores them over the content) instead of
                                // stretching the page.
                                ...(renderKind === 'HTML' ? { maxHeight: height, overflowY: 'auto' as const } : {}),
                            }}
                        >
                            {renderKind === 'IMAGE' && (
                                <RotatableImage
                                    url={url}
                                    height={height}
                                    maxWidth={maxImageWidth}
                                    rotation={rotation}
                                    overlay={overlay}
                                />
                            )}
                            {isFetching && (
                                <Box
                                    sx={{
                                        height,
                                        width: renderKind === 'HTML' ? Math.round(contentWidth * htmlZoom) : 600,
                                        minWidth: renderKind === 'HTML' ? undefined : 400,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: '#fff',
                                    }}
                                    data-test={`${testIdPrefix}.loading`}
                                >
                                    <CircularProgress />
                                </Box>
                            )}
                            {renderKind === 'HTML' && htmlContent && (
                                <ShadowHtmlContent
                                    htmlContent={htmlContent}
                                    width={contentWidth}
                                    zoom={htmlZoom}
                                    testIdPrefix={testIdPrefix}
                                />
                            )}
                            {renderKind === 'PDF' && objectUrl && (
                                <embed
                                    src={objectUrl}
                                    type="application/pdf"
                                    style={{ height, width: 600, minWidth: 400 }}
                                />
                            )}
                            {renderKind !== 'IMAGE' && overlay}
                        </TransformComponent>
                    </Fragment>
                )}
            </TransformWrapper>
        </Box>
    );
};

export default DocumentViewer;
